import { useState, type ReactNode } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

import { ThinkingIndicator } from "./thinking-indicator";
import type { Phase } from "./phase-grouping";

interface PhaseGroupProps {
  phase: Phase;
  /** First/last in the turn — controls how far the connector rail extends. */
  isFirst: boolean;
  isLast: boolean;
  /** True when this is the turn's last phase AND the message is still streaming. */
  isLivePhase: boolean;
  /** Short label for the in-flight action, e.g. "writing KioskProvider.tsx". */
  liveActionLabel?: string;
  /**
   * Renders one of this phase's segments. The caller (AgentMessage) owns the
   * actual segment JSX (text + tool groups + inline HITL cards); we just place
   * it. `globalIdx` is the segment's index in the full turn so "is last overall"
   * math stays correct.
   */
  renderSegment: (
    segment: Phase["segments"][number],
    globalIdx: number,
  ) => ReactNode;
}

// Gutter holds the connector rail + node; node center sits at GUTTER/2 = RAIL_X.
const GUTTER = "w-6"; // 24px — matches the OptiDev mark width so labels align
const RAIL_X = "left-3"; // 12px — center of the gutter
const NODE_TOP = "top-4"; // node vertical center from the row top
const BODY_INDENT = "pl-8"; // label/body column (gutter 24 + gap 8)

/**
 * One phase of an assistant turn, drawn as a node on a vertical connector rail.
 *
 * Sectioning: each step is a node; the rail segment between nodes separates
 * them, and an expanded step's body lives between its node and the next (so it
 * unambiguously belongs to that step). Markers: a quiet hollow ring for done
 * steps (no loud green); the OptiDev mark (shimmering) for the live step — the
 * mark always means "happening now".
 *
 * Expansion: defaults to `isLast` — the last step is open by default (while live
 * AND once the turn finishes, so its conclusion stays visible); every earlier
 * step collapses (with a grid-rows height animation) as the next begins. A click
 * overrides the default either way.
 */
export function PhaseGroup({
  phase,
  isFirst,
  isLast,
  isLivePhase,
  liveActionLabel,
  renderSegment,
}: PhaseGroupProps) {
  // null = follow the default (open iff last); a click pins it open/closed.
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? isLast;

  const body = (
    // pt separates the body prose from the phase label above it; pb gives the
    // expanded body breathing room before the next phase's title (kept modest —
    // a tool-ending phase already adds the tool block's own mb below its tools).
    <div className={cn("space-y-0 pt-1.5 pb-1 pr-2", BODY_INDENT)}>
      {phase.segments.map((segment, localIdx) =>
        renderSegment(segment, phase.startIndex + localIdx),
      )}
    </div>
  );

  // Animated collapse: grid-template-rows 1fr↔0fr (height-agnostic); inner clips.
  const collapsibleBody = (
    <div
      className={cn(
        "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
        expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div className="overflow-hidden">{body}</div>
    </div>
  );

  // Connector rail: starts at the first node, ends at the last node, spans full
  // height in between — so it threads the nodes and fences each step's body.
  //
  // The live phase's node is the OptiDev mark (24px SVG), whose cloud interior is
  // transparent — a rail drawn to its center (16px) would show *through* the gaps
  // in the logo. So for the live phase the rail stops at the logo's top edge
  // (the row's py-1.5 = 6px) and never crosses it; if it's also the only phase
  // there's no node above to connect to, so the rail is dropped entirely.
  const rail = (
    <span
      aria-hidden
      className={cn(
        "absolute z-0 w-px -translate-x-1/2 bg-border",
        RAIL_X,
        isFirst ? NODE_TOP : "top-0",
        isLivePhase
          ? isFirst
            ? "hidden"
            : "h-1.5"
          : isLast
            ? "h-4"
            : "bottom-0",
      )}
    />
  );

  return (
    <div
      className={cn(
        "relative",
        // The closing node ("Wrap-up" / "Over to you") is split out only when the
        // turn finishes, so it mounts fresh — fade + slide it in once so the
        // reveal reads as intentional rather than a snap. Stable key → plays once.
        phase.kind === "closing" &&
          "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-300",
      )}
    >
      {rail}

      {isLivePhase ? (
        // Live: OptiDev mark (sits on the rail) + shimmer label. The mark is the
        // node. ThinkingIndicator's 24px mark aligns with the hollow-ring gutter.
        <div className="flex items-center py-1.5">
          <ThinkingIndicator
            label={
              liveActionLabel
                ? `${phase.runningLabel} · ${liveActionLabel}`
                : phase.runningLabel
            }
          />
          {phase.toolCount > 0 && (
            <span className="ml-auto pr-2 text-xs tabular-nums text-muted-foreground">
              {phase.toolCount}
            </span>
          )}
        </div>
      ) : (
        // Finished: hollow-ring node + click-to-expand label.
        <button
          type="button"
          onClick={() => setUserExpanded(!expanded)}
          className="group flex w-full items-center gap-2 rounded-md py-1.5 pr-2 text-left transition-colors hover:bg-muted/30"
        >
          <span className={cn("relative z-10 flex shrink-0 justify-center", GUTTER)}>
            <span
              className={cn(
                "h-2.5 w-2.5 rounded-full border-[1.5px] bg-background",
                phase.status === "error"
                  ? "border-amber-500"
                  : "border-muted-foreground/50",
              )}
            />
          </span>

          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {phase.label}
            {phase.outcome && (
              <span className="ml-2 font-normal text-muted-foreground">
                {phase.outcome}
              </span>
            )}
          </span>

          {phase.toolCount > 0 && (
            <span className="shrink-0 pl-2 text-xs tabular-nums text-muted-foreground/70 opacity-0 transition-opacity group-hover:opacity-100">
              {phase.toolCount} steps
            </span>
          )}
          {expanded ? (
            <ChevronDown className="ml-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground" />
          ) : (
            <ChevronRight className="ml-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground" />
          )}
        </button>
      )}

      {collapsibleBody}
    </div>
  );
}
